import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FileDown, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { fleetImportApi } from '../lib/fleetImportApi';
import { organizationsAPI } from '../lib/api';
import { messageErreur } from '../lib/erreurs';
import { formatDateHeure } from '../lib/formats';
import { useAuth } from '../context/AuthContext';
import { PageHeader } from '../components/ui/page-header';
import { Button } from '../components/ui/button';
import { SearchableSelect } from '../components/ui/searchable-select';
import { StatutBadge } from '../components/ui/StatutBadge';
import { LoadingState } from '../components/ui/loading-state';
import CorrespondanceColonnes from '../components/fleetImport/CorrespondanceColonnes';
import TableauBrouillon from '../components/fleetImport/TableauBrouillon';
import RapportImport from '../components/fleetImport/RapportImport';

/**
 * Importer des camions et des chauffeurs depuis un fichier Excel.
 *
 * Dépôt -> analyse en arrière-plan (on peut quitter la page et revenir) ->
 * brouillon à relire (correspondance des colonnes, cellules, lignes) ->
 * confirmation -> rapport ligne par ligne. L'administrateur de la société
 * arrive par `/fleet-import` ; le super administrateur par la fiche d'une
 * société (`/organizations/:id/fleet-import`), ou choisit la société ici.
 */

const SCRUTATION_MS = 3000;
const EN_COURS = ['queued', 'analyzing', 'confirmed', 'importing'];

const pluriel = (nombre, mot) => `${nombre} ${mot}${nombre > 1 ? 's' : ''}`;

const FleetImportPage = () => {
  const { id: idRoute } = useParams();
  const { isSuperAdmin } = useAuth();
  const champ = useRef(null);
  const [societes, setSocietes] = useState([]);
  const [societeChoisie, setSocieteChoisie] = useState('');
  const organisation = isSuperAdmin ? idRoute || societeChoisie : undefined;
  const prete = !isSuperAdmin || Boolean(organisation);

  const [job, setJob] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [occupe, setOccupe] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin || idRoute) return undefined;
    let actif = true;
    organizationsAPI
      .getEvery()
      .then((liste) => actif && setSocietes(liste || []))
      .catch((erreur) => toast.error(messageErreur(erreur, 'Impossible de charger les sociétés')));
    return () => {
      actif = false;
    };
  }, [isSuperAdmin, idRoute]);

  const charger = useCallback(async () => {
    if (!prete) {
      setChargement(false);
      return;
    }
    setChargement(true);
    try {
      setJob(await fleetImportApi.current(organisation));
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de lire l’import en cours'));
    } finally {
      setChargement(false);
    }
  }, [organisation, prete]);

  useEffect(() => {
    charger();
  }, [charger]);

  // Analyse et enregistrement tournent sur le serveur : on relit l'état tant qu'ils durent.
  const jobId = job?.id;
  const statut = job?.status;
  useEffect(() => {
    if (!jobId || !EN_COURS.includes(statut)) return undefined;
    const minuteur = setInterval(async () => {
      try {
        setJob(await fleetImportApi.get(jobId));
      } catch (erreur) {
        // Une lecture manquée est sans conséquence : la suivante reprend.
      }
    }, SCRUTATION_MS);
    return () => clearInterval(minuteur);
  }, [jobId, statut]);

  const executer = async (action, message) => {
    setOccupe(true);
    try {
      setJob(await action());
    } catch (erreur) {
      toast.error(messageErreur(erreur, message));
    } finally {
      setOccupe(false);
    }
  };

  const deposer = (evenement) => {
    const fichier = evenement.target.files?.[0];
    evenement.target.value = '';
    if (fichier) executer(() => fleetImportApi.upload(fichier, organisation), 'Impossible de lire le fichier');
  };

  const modele = async () => {
    try {
      await fleetImportApi.template();
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de télécharger le modèle'));
    }
  };

  const camions = job?.summary?.trucks?.included || 0;
  const chauffeurs = job?.summary?.drivers?.included || 0;
  const termine = job && ['done', 'failed', 'cancelled'].includes(job.status);
  const nouvelImport = !job || termine;

  return (
    <div className="mx-auto w-full max-w-[1800px] space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8" data-testid="fleet-import-page">
      <PageHeader
        eyebrow="Ressources"
        title="Importer des camions et des chauffeurs"
        description="Déposez votre fichier Excel : les colonnes sont reconnues pour vous, vous relisez le brouillon, puis vous confirmez. Rien n’est enregistré avant la confirmation."
        backTo={isSuperAdmin && idRoute ? `/organizations/${idRoute}` : '/trucks'}
        backLabel={isSuperAdmin && idRoute ? 'Retour à la société' : 'Retour aux camions'}
        meta={job ? <StatutBadge domaine="import_flotte_travail" code={job.status} /> : null}
        actions={
          <Button type="button" variant="outline" onClick={modele} data-testid="fleet-import-template">
            <FileDown className="h-4 w-4" aria-hidden="true" />
            Modèle Excel
          </Button>
        }
      />

      {isSuperAdmin && !idRoute && (
        <div className="max-w-md" data-testid="fleet-import-org">
          <SearchableSelect
            value={societeChoisie}
            onValueChange={setSocieteChoisie}
            options={societes.map((societe) => ({ value: societe.id, label: societe.name }))}
            placeholder="Choisir la société"
            searchPlaceholder="Rechercher une société…"
            emptyMessage="Aucune société."
          />
        </div>
      )}

      {chargement && prete && <LoadingState shape="cards" />}

      {!chargement && prete && nouvelImport && (
        <div className="space-y-4">
          {job?.status === 'failed' && (
            <div role="alert" className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm" data-testid="fleet-import-failed">
              {job.error_message}
            </div>
          )}
          {job?.status === 'done' && <RapportImport job={job} />}
          <div className="flex flex-wrap items-center gap-3">
            <input ref={champ} type="file" accept=".xlsx,.csv" className="sr-only" onChange={deposer} data-testid="fleet-import-file" />
            <Button type="button" onClick={() => champ.current?.click()} disabled={occupe} data-testid="fleet-import-choose">
              {occupe ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Upload className="h-4 w-4" aria-hidden="true" />}
              {job ? 'Nouvel import' : 'Choisir un fichier'}
            </Button>
          </div>
        </div>
      )}

      {!chargement && job && EN_COURS.includes(job.status) && (
        <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-6" role="status" data-testid="fleet-import-progress">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="font-medium">{job.status === 'queued' || job.status === 'analyzing' ? 'Analyse en cours' : 'Import en cours'}</span>
          <span className="text-sm text-muted-foreground">{job.filename}</span>
        </div>
      )}

      {!chargement && job?.status === 'draft' && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="font-medium">{job.filename}</span>
            <span className="tabular-nums text-muted-foreground">{formatDateHeure(job.created_at)}</span>
            {(job.warnings || []).map((avertissement) => (
              <span key={avertissement} className="rounded-full border border-flame/30 bg-flame/10 px-2.5 py-0.5 text-xs text-flame-ink">
                {avertissement}
              </span>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto"
              disabled={occupe}
              onClick={() => executer(() => fleetImportApi.cancel(job.id), 'Impossible d’annuler l’import')}
              data-testid="fleet-import-cancel"
            >
              Annuler l’import
            </Button>
          </div>
          <CorrespondanceColonnes
            job={job}
            disabled={occupe}
            onChange={(entrees) => executer(() => fleetImportApi.updateMapping(job.id, entrees), 'Impossible de changer la correspondance')}
          />
          <TableauBrouillon job={job} disabled={occupe} onJob={setJob} />
          <div className="sticky bottom-0 flex justify-end border-t border-border bg-background/95 py-3 backdrop-blur">
            <Button
              type="button"
              size="lg"
              disabled={occupe || camions + chauffeurs === 0}
              onClick={() => executer(() => fleetImportApi.confirm(job.id, job.draft_version), 'Impossible de lancer l’import')}
              data-testid="fleet-import-confirm"
            >
              {occupe && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {`Importer ${pluriel(camions, 'camion')} et ${pluriel(chauffeurs, 'chauffeur')}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetImportPage;
